import { useEffect, useRef, useState } from 'react'
import { getTeamleden, upsertTeamlid, deleteTeamlid, uploadTeamFoto, supabase } from '../lib/supabase'

function TeamlidForm({ lid, onSave, onCancel }) {
  const [naam, setNaam] = useState(lid?.naam || '')
  const [functie, setFunctie] = useState(lid?.functie || '')
  const [motivatie, setMotivatie] = useState(lid?.motivatie || '')
  const [volgorde, setVolgorde] = useState(lid?.volgorde ?? 0)
  const [fotoUrl, setFotoUrl] = useState(lid?.foto_url || '')
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [fout, setFout] = useState('')
  const fileRef = useRef()

  const lidId = lid?.id || crypto.randomUUID()

  async function handleFoto(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true); setFout('')
    try {
      const url = await uploadTeamFoto(lidId, file)
      setFotoUrl(url)
    } catch (err) {
      setFout('Foto upload mislukt: ' + err.message)
    } finally {
      setUploading(false)
    }
  }

  async function handleOpslaan() {
    if (!naam.trim()) { setFout('Naam is verplicht.'); return }
    setSaving(true); setFout('')
    try {
      await upsertTeamlid({ id: lidId, naam, functie, motivatie, foto_url: fotoUrl, volgorde: Number(volgorde) })
      onSave()
    } catch (err) {
      setFout('Opslaan mislukt: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ background: 'var(--bg)', border: '1.5px solid var(--orange)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 12 }}>
      <p style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
        {lid ? 'Bewerken' : 'Nieuw teamlid'}
      </p>

      {/* Foto upload */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          {fotoUrl ? (
            <img src={fotoUrl} alt="preview" style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--green)' }} />
          ) : (
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--green-light)', border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
            </div>
          )}
          <button onClick={() => fileRef.current.click()} disabled={uploading} style={{ position: 'absolute', bottom: 0, right: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--orange)', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
            {uploading
              ? <div style={{ width: 8, height: 8, border: '1.5px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            }
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFoto} />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
            {uploading ? 'Bezig met uploaden…' : 'Tik op het oranje icoon om een foto te uploaden'}
          </p>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Naam</label>
        <input value={naam} onChange={e => setNaam(e.target.value)} placeholder="Volledige naam" />
      </div>
      <div className="form-group">
        <label className="form-label">Functie</label>
        <input value={functie} onChange={e => setFunctie(e.target.value)} placeholder="bijv. Personal trainer" />
      </div>
      <div className="form-group">
        <label className="form-label">Motivatie</label>
        <textarea value={motivatie} onChange={e => setMotivatie(e.target.value)} placeholder="Persoonlijke motivatie van dit teamlid…" style={{ minHeight: 80 }} />
      </div>
      <div className="form-group">
        <label className="form-label">Volgorde</label>
        <input type="number" min="0" value={volgorde} onChange={e => setVolgorde(e.target.value)} style={{ width: 80 }} />
      </div>

      {fout && <p className="error-msg" style={{ marginBottom: 8 }}>{fout}</p>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={handleOpslaan} disabled={saving} style={{ flex: 1 }}>
          {saving ? 'Opslaan…' : 'Opslaan'}
        </button>
        <button className="btn-ghost" onClick={onCancel} style={{ flex: 1 }}>Annuleren</button>
      </div>
    </div>
  )
}

export default function AdminTeamTab() {
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)
  const [bewerkId, setBewerkId] = useState(null) // id of 'nieuw'
  const [fout, setFout] = useState('')

  useEffect(() => { laden() }, [])

  async function laden() {
    setLoading(true)
    try {
      const data = await getTeamleden()
      setTeam(data)
    } catch (e) {
      setFout('Laden mislukt: ' + e.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleVerwijder(id) {
    if (!window.confirm('Teamlid verwijderen?')) return
    await deleteTeamlid(id)
    laden()
  }

  if (loading) return <div className="spinner" />

  return (
    <div>
      {/* Nieuw toevoegen knop */}
      {bewerkId !== 'nieuw' && (
        <button
          className="btn-primary"
          style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          onClick={() => setBewerkId('nieuw')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Teamlid toevoegen
        </button>
      )}

      {/* Nieuw formulier */}
      {bewerkId === 'nieuw' && (
        <TeamlidForm
          lid={null}
          onSave={() => { setBewerkId(null); laden() }}
          onCancel={() => setBewerkId(null)}
        />
      )}

      {fout && <p className="error-msg" style={{ marginBottom: 10 }}>{fout}</p>}

      {/* Bestaande teamleden */}
      {team.length === 0 && bewerkId !== 'nieuw' && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, padding: '24px 16px' }}>
          Nog geen teamleden. Voeg je eerste teamlid toe!
        </div>
      )}

      {team.map(lid => (
        <div key={lid.id}>
          {bewerkId === lid.id ? (
            <TeamlidForm
              lid={lid}
              onSave={() => { setBewerkId(null); laden() }}
              onCancel={() => setBewerkId(null)}
            />
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px' }}>
                {/* Avatar */}
                {lid.foto_url ? (
                  <img src={lid.foto_url} alt={lid.naam} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid var(--green)' }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--green)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat', fontSize: 16, fontWeight: 800, flexShrink: 0 }}>
                    {lid.naam.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                )}
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: 'Montserrat', fontSize: 13, fontWeight: 800, color: 'var(--text)', marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lid.naam}</p>
                  <p style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>{lid.functie}</p>
                  {lid.motivatie && (
                    <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {lid.motivatie}
                    </p>
                  )}
                </div>
              </div>
              {/* Acties */}
              <div style={{ display: 'flex', borderTop: '1px solid var(--border)' }}>
                <button
                  onClick={() => setBewerkId(lid.id)}
                  style={{ flex: 1, padding: '9px', background: 'transparent', border: 'none', borderRight: '1px solid var(--border)', cursor: 'pointer', fontSize: 12, color: 'var(--green)', fontWeight: 600, fontFamily: 'Montserrat' }}
                >
                  ✏️ Bewerken
                </button>
                <button
                  onClick={() => handleVerwijder(lid.id)}
                  style={{ flex: 1, padding: '9px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--red)', fontFamily: 'Open Sans' }}
                >
                  🗑 Verwijderen
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}